import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Star, ShoppingCart, Plus, Minus } from "lucide-react";
import { useCart } from "../context/cartContext";

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { cartItems, addToCart, updateQuantity, removeFromCart } = useCart();

  const discount = product.discount || 0;
  const originalPrice = Number(product.price);
  const discountedPrice =
    discount > 0
      ? (originalPrice * (100 - discount)) / 100
      : originalPrice;

  // Проверяем, есть ли товар в корзине
  const cartItem = cartItems.find((item) => item.id === product.id);

  // Форматирование цены как на макете
  const formatPrice = (price) => {
    return Math.round(price).toLocaleString('ru-RU') + " ₽";
  };

  // Функция для проверки рейтинга
  const shouldShowRating = () => {
    if (!product.rating) return false;
    
    const rating = parseFloat(product.rating);
    return !isNaN(rating) && rating > 0;
  };

  // Обработчик клика по карточке
  const handleCardClick = (e) => {
    // Если клик был по кнопке "В корзину" или счетчику, не переходим на страницу товара
    if (e.target.closest('.cart-button') || e.target.closest('.quantity-control')) {
      return;
    }
    navigate(`/product/${product.id}`, {
      state: { product },
    });
  };

  // Обработчик добавления в корзину
  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (cartItem) {
      updateQuantity(product.id, cartItem.quantity + 1);
    } else {
      addToCart(product);
    }
  };

  // Обработчик уменьшения количества
  const handleDecrease = (e) => {
    e.stopPropagation();
    if (cartItem.quantity === 1) {
      removeFromCart(product.id);
    } else {
      updateQuantity(product.id, cartItem.quantity - 1);
    }
  };

  // Обработчик увеличения количества
  const handleIncrease = (e) => {
    e.stopPropagation();
    updateQuantity(product.id, cartItem.quantity + 1);
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 flex flex-col h-full min-h-[320px] max-h-[420px] cursor-pointer overflow-hidden hover:shadow-md transition-shadow"
    >
      {/* Картинка */}
      <div className="w-full aspect-[4/3] overflow-hidden rounded-lg relative">
        <img
          src={product.image}
          alt={product.title}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
        />
        {/* Бейдж скидки если есть */}
        {discount > 0 && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded font-bold">
            -{discount}%
          </div>
        )}
      </div>

      {/* Заголовок и краткое описание */}
      <div className="flex-1 mt-4 min-h-[35px]">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h2 className="text-base font-bold line-clamp-2 leading-snug break-words text-gray-900">
              {product.title}
            </h2>
            <p className="text-gray-600 text-sm mt-1 line-clamp-2 leading-tight">
              {product.shortDescription || product.description}
            </p>
          </div>
        </div>
      </div>

      {/* Цена - как на макете */}
      <div className="mt-3">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-xl font-bold text-gray-900">
            {formatPrice(discountedPrice)}
          </span>
          {discount > 0 && (
            <>
              <span className="text-base text-gray-400 line-through">
                {formatPrice(originalPrice)}
              </span>
            </>
          )}
        </div>

        {/* Рейтинг */}
        {shouldShowRating() && (
          <div className="flex items-center text-sm text-gray-500 gap-1 mt-1">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <span>{product.rating}</span>
            <span className="text-gray-400">
              ({product.reviews || "0"})
            </span>
          </div>
        )}
      </div>

      {/* Кнопка добавления в корзину/управление количеством */}
      <div className="mt-2 pt-3 border-t border-gray-100">
        {cartItem ? (
          // Если товар уже в корзине - показываем счетчик как на изображении
          <div className="flex items-center justify-between quantity-control">
            <button
              onClick={handleDecrease}
              className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 transition-colors"
            >
              <Minus className="w-4 h-4" />
            </button>
            
            <div className="flex flex-col items-center">
              <span className="font-bold text-[14px] text-gray-900">
                {cartItem.quantity}
              </span>
              <span className="text-xs text-gray-500">в корзине</span>
            </div>
            
            <button
              onClick={handleIncrease}
              className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        ) : (
          // Если товара нет в корзине - простая кнопка добавления
          <button
            onClick={handleAddToCart}
            className="cart-button w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white py-3 rounded-lg text-sm font-semibold transition-all active:scale-[0.98] shadow-sm hover:shadow"
          >
            В корзину
          </button>
        )}
      </div>
    </div>
  );
};

// Компонент для отображения корзины в нижней части экрана
export const CartBottomBar = () => {
  const { cartItems } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cartItems.reduce((sum, item) => {
    const discount = item.discount || 0;
    const originalPrice = Number(item.price);
    const discountedPrice = discount > 0 ? 
      (originalPrice * (100 - discount)) / 100 : 
      originalPrice;
    return sum + (discountedPrice * item.quantity);
  }, 0);
  
  // Форматирование цены
  const formatPrice = (price) => {
    return Math.round(price).toLocaleString('ru-RU') + " ₽";
  };
  
  // Функция для правильного склонения
  const getItemsText = (count) => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;
    
    if (lastDigit === 1 && lastTwoDigits !== 11) return 'товар';
    if (lastDigit >= 2 && lastDigit <= 4 && !(lastTwoDigits >= 12 && lastTwoDigits <= 14)) return 'товара';
    return 'товаров';
  };
  
  // Обработчик открытия корзины
  const handleOpenCart = () => {
    navigate("/cart");
  };
  
  // Скрываем на странице корзины И на странице товара
  const hideOnPages = ["/cart", "/product/"];
  const shouldHide = hideOnPages.some(path => location.pathname.startsWith(path));
  
  if (shouldHide || totalItems === 0) return null;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {/* Фоновый блок, который заполняет все пространство снизу */}
      <div className="h-4"></div>
      
      {/* Основная кнопка */}
      <div className="max-w-7xl mx-auto px-4 pb-3">
        <div 
          onClick={handleOpenCart}
          className="bg-[#0891B2] rounded-2xl flex items-center justify-between text-white py-3 px-8 cursor-pointer transition-all active:scale-[0.99] shadow-lg"
        >
          {/* Левая часть: количество товаров */}
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium text-gray-300">
              {totalItems} {getItemsText(totalItems)}
            </span>
          </div>
          
          {/* Центральная часть: текст "Открыть корзину" */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <span className="font-bold text-[15px] whitespace-nowrap">
              Открыть корзину
            </span>
          </div>
          
          {/* Правая часть: сумма */}
          <div className="flex flex-col items-end">
            <span className="font-bold text-xs">
              {formatPrice(totalPrice)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;